import { SearchIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { useDeleteAllProviders } from 'components/hooks/member_onboarding_v2/useDeleteAllProviders'
import { ExpectedPlanUtilizationV2 } from 'components/plan_selection_preference/ExpectedPlanUtilizationV2'
import { MaximumOutOfPocketBudgetV2 } from 'components/plan_selection_preference/MaximumOutOfPocketBudgetV2'
import { useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import { useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlans as useGetMedicalPlans } from 'utils/web_api'
import { type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansExpectedPlanUtilization as ExpectedPlanUtilization, type MemberOnboardingProvider } from 'utils/web_api.schemas'

import { useOnboarding } from '../OnboardingContext'
import { MultistepFilters } from './MultistepFilters'
import ProviderSearchV2 from './ProviderSearchV2'

export interface RecommendationSearchFilters {
  expectedPlanUtilization?: ExpectedPlanUtilization | null;
  oopBudget?: number | null;
  providers?: MemberOnboardingProvider[] | null;
}

interface RecommendationSearchProps {
  allowanceAmount?: GrapeMoney | null;
  onSearch: (filters: RecommendationSearchFilters) => void;
}

export function RecommendationQuestionBar ({
  onSearch,
}: RecommendationSearchProps) {
  const { onboardingData, enrollmentEventId } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()

  const [activeFilter, setActiveFilter] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const listRef = useRef<HTMLDivElement>(null)

  // Draft filters are used to store the filters that the user has entered,
  // but may have not been actually applied for the actual search triggered by the user via onSearch.
  const [draftFilters, setDraftFilters] = useState<RecommendationSearchFilters>(
    {}
  )

  // Debounce the oopBudget value as it comes from a slider
  const [debouncedOopBudget] = useDebounceValue<number | null>(
    draftFilters.oopBudget ?? null,
    500
  )

  // Filters are used to store the filters that are actually applied for the actual search triggered by the user via onSearch.
  const [filters, setFilters] = useState<RecommendationSearchFilters>({})

  useEffect(() => {
    if (onboardingData?.medical_plan_selection_preferences) {
      // Update filter values from the onboarding API.
      const newFilters = {
        expectedPlanUtilization:
          onboardingData.medical_plan_selection_preferences
            .expected_plan_utilization,
        oopBudget: onboardingData.medical_plan_selection_preferences.oop_budget,
      }
      setDraftFilters({ ...draftFilters, ...newFilters })
      setFilters({ ...filters, ...newFilters })
    }
  }, [onboardingData?.medical_plan_selection_preferences])

  useEffect(() => {
    if (onboardingData?.provider_preferences) {
      // Update filter values from the onboarding API.
      setDraftFilters((prev) => ({
        ...prev,
        providers: onboardingData.provider_preferences,
      }))
      setFilters((prev) => ({
        ...prev,
        providers: onboardingData.provider_preferences,
      }))
    }
  }, [onboardingData?.provider_preferences])

  const { data: medicalPlanData, isLoading: isLoadingPlan } =
    useGetMedicalPlans(
      enrollmentEventId,
      {
        expected_plan_utilization:
          draftFilters.expectedPlanUtilization || undefined,
        oop_budget: debouncedOopBudget ?? undefined,
      },
      {
        query: {
          retry: false,
          queryKey: [
            'enrollmentEvent',
            enrollmentEventId,
            'medical_plans',
            draftFilters.expectedPlanUtilization,
            debouncedOopBudget,
          ],
        },
      }
    )

  const { mutate: deleteAllProviders } = useDeleteAllProviders({
    enrollmentEventId,
  })

  const allowanceAmount = onboardingData?.allowance?.allowance_amount
  const allowanceAmountInDollars = allowanceAmount?.subunits
    ? allowanceAmount.subunits / 100
    : 0
  const allowanceAmountForMaxOOP = allowanceAmountInDollars.toFixed(2)
  const planCount = medicalPlanData?.pagination?.total_records
  const zipcode = onboardingData?.member?.zip

  const getViewPlanLabel = () => {
    if (planCount && planCount > 1) {
      return `View ${planCount} plans`
    } else if (planCount === 1) {
      return 'View 1 plan'
    } else {
      return 'No plans available'
    }
  }

  const { expectedPlanUtilization, oopBudget, providers } = editing
    ? draftFilters
    : filters

  const getPlanUtilizationLabel = () => {
    if (expectedPlanUtilization === 'low') {
      return 'A little'
    } else if (expectedPlanUtilization === 'medium') {
      return 'Somewhat often'
    } else if (expectedPlanUtilization === 'high') {
      return 'A lot'
    } else {
      return undefined
    }
  }

  const getOopBudgetLabel = () => {
    if (typeof oopBudget === 'number' && oopBudget >= 0) {
      return `Up to $${oopBudget}/mo`
    } else {
      return undefined
    }
  }

  const getProvidersLabel = () => {
    return providers && providers.length > 0
      ? `${providers.length} ${providers.length > 1 ? 'providers' : 'provider'}`
      : undefined
  }

  const handleSearch = () => {
    if (editing) {
      setFilters(draftFilters)
      onSearch(draftFilters)
    } else {
      setEditing(true)
      setActiveFilter('expected_plan_utilization')
    }
  }

  const handleClearProviders = () => {
    deleteAllProviders(undefined, {
      onSuccess: () => {
        setDraftFilters((prev) => ({
          ...prev,
          providers: [],
        }))
        // providers are actually deleted from the backend, so we need to clear the filters
        setFilters((prev) => ({
          ...prev,
          providers: [],
        }))
        if (activeFilter !== 'providers') {
          setActiveFilter('providers')
        }
        if (!editing) {
          setEditing(true)
        }
      },
    })
  }

  const handleEditingChange = (editing: boolean) => {
    setEditing(editing)

    if (editing && !breakpointIsAtLeast('xs') && listRef.current) {
      listRef.current.style.scrollMarginTop = '68px'
      listRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const button = (
    <MultistepFilters.Button
      leftIcon={!editing && !isLoadingPlan ? SearchIcon : undefined}
      onClick={() => {
        handleSearch()
      }}
      disabled={isLoadingPlan}
    >
      {!isLoadingPlan && !editing && 'Search'}
      {!isLoadingPlan && editing && getViewPlanLabel()}
      {isLoadingPlan && 'Searching...'}
    </MultistepFilters.Button>
  )

  function renderFilterContent (children: React.ReactNode) {
    return (
      <>
        <div className='p-4'>{children}</div>
        {!breakpointIsAtLeast('xs') && (
          <div className='mt-2 bg-white p-2 border-t border-gray-200 flex justify-end sticky bottom-0'>{button}</div>
        )}
      </>
    )
  }

  return (
    <MultistepFilters
      activeFilter={activeFilter}
      editing={editing}
      onActiveFilterChange={setActiveFilter}
      onEditingChange={handleEditingChange}
      zIndex={editing ? 11 : undefined}
    >
      <MultistepFilters.List ref={listRef}>
        <MultistepFilters.Filter
          label='Insurance usage'
          name='expected_plan_utilization'
          onClearValue={() => {
            setDraftFilters((prev) => ({
              ...prev,
              expectedPlanUtilization: undefined,
            }))
            if (activeFilter !== 'expected_plan_utilization') {
              setActiveFilter('expected_plan_utilization')
            }
            if (!editing) {
              setEditing(true)
            }
          }}
          placeholder='Add frequency'
          value={getPlanUtilizationLabel()}
        >
          {renderFilterContent(
            <ExpectedPlanUtilizationV2
              value={expectedPlanUtilization}
              setValue={(value: ExpectedPlanUtilization | null | undefined) => {
                setDraftFilters((prev) => ({
                  ...prev,
                  expectedPlanUtilization: value,
                }))
              }}
            />
          )}
        </MultistepFilters.Filter>
        <MultistepFilters.Filter
          label='Max contribution'
          name='oopBudget'
          onClearValue={() => {
            setDraftFilters((prev) => ({
              ...prev,
              oopBudget: undefined,
            }))
            if (activeFilter !== 'oopBudget') {
              setActiveFilter('oopBudget')
            }
            if (!editing) {
              setEditing(true)
            }
          }}
          placeholder='Add contribution'
          value={getOopBudgetLabel()}
        >
          {renderFilterContent(
            <MaximumOutOfPocketBudgetV2
              value={oopBudget}
              setValue={(value: number | null | undefined) => {
                setDraftFilters((prev) => ({
                  ...prev,
                  oopBudget: value,
                }))
              }}
              allowanceAmount={{
                value: allowanceAmountForMaxOOP,
                currency: 'USD',
              }}
            />
          )}
        </MultistepFilters.Filter>
        <MultistepFilters.Filter
          label='Providers'
          name='providers'
          placeholder='Add provider'
          value={getProvidersLabel()}
          onClearValue={handleClearProviders}
        >
          {renderFilterContent(
            zipcode && (
              <ProviderSearchV2
                zip={zipcode}
                providers={providers}
                onSelect={(providers) => {
                  setDraftFilters((prev) => ({
                    ...prev,
                    providers,
                  }))
                }}
              />
            )
          )}
        </MultistepFilters.Filter>
      </MultistepFilters.List>
      {breakpointIsAtLeast('xs') && button}
    </MultistepFilters>
  )
}
